import { useLayoutEffect, useState } from 'react';

import { type ColumnPromotionInput, type ColumnPromotionResult, computeColumnPromotion } from './columnLayout';

export type ColumnPromotionConfig = Omit<ColumnPromotionInput, 'containerWidth'>;

const sameResult = (current: ColumnPromotionResult | undefined, next: ColumnPromotionResult) => {
  if (!current) {
    return false;
  }
  if (current.promotedIds.length !== next.promotedIds.length || current.foldedIds.length !== next.foldedIds.length) {
    return false;
  }
  if (current.promotedIds.some((id, index) => id !== next.promotedIds[index])) {
    return false;
  }
  if (current.foldedIds.some((id, index) => id !== next.foldedIds[index])) {
    return false;
  }
  const currentIds = Object.keys(current.widths);
  const nextIds = Object.keys(next.widths);
  return currentIds.length === nextIds.length && currentIds.every((id) => current.widths[id] === next.widths[id]);
};

function observeColumnPromotionForKey(
  element: HTMLElement,
  config: ColumnPromotionConfig,
  configKey: string,
  setResult: (updater: (current: ColumnPromotionResult | undefined) => ColumnPromotionResult | undefined) => void,
) {
  if (configKey === 'null') {
    return () => undefined;
  }

  let frame = 0;
  const measure = () => {
    frame = 0;
    const containerWidth = Math.floor(
      element.getBoundingClientRect().width - (element.offsetWidth - element.clientWidth),
    );
    if (containerWidth <= 0) {
      setResult(() => undefined);
      return;
    }
    const next = computeColumnPromotion({ ...config, containerWidth });
    setResult((current) => (sameResult(current, next) ? current : next));
  };
  const schedule = () => {
    if (!frame) {
      frame = requestAnimationFrame(measure);
    }
  };

  measure();
  if (typeof ResizeObserver === 'undefined') {
    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }

  const observer = new ResizeObserver(schedule);
  observer.observe(element);
  return () => {
    observer.disconnect();
    if (frame) {
      cancelAnimationFrame(frame);
    }
  };
}

export function useColumnPromotion(element: HTMLElement | null, config: ColumnPromotionConfig | undefined) {
  const [result, setResult] = useState<ColumnPromotionResult>();
  const configKey = JSON.stringify(config ?? null);

  if ((!element || !config) && result !== undefined) {
    setResult(undefined);
  }

  useLayoutEffect(() => {
    if (!element || !config) {
      return;
    }
    return observeColumnPromotionForKey(element, config, configKey, setResult);
  }, [element, config, configKey]);

  return configKey === 'null' ? undefined : result;
}
